import {
  type AiQueryTraceEntry,
  pendingCall,
  settlePendingCall,
  traceToSteps,
} from 'components/ai/aiQueryConversation';
import {
  adoptSpeculativeAiQuery,
  type AiQueryResult,
  type AiQueryStreamCallbacks,
  markSpeculativeAiQuerySeen,
  streamAiQuery,
} from 'views/admin/adminAiQueryApi';

/**
 * Speculative Ask: global search starts the Ask turn while a question is still
 * being typed, so it is partway answered when the user presses Enter. The server
 * holds the turn back from history, analytics and write tools until it is sent
 * (`helpers/ai_query/speculative.py`); this side keeps at most one per tab,
 * records its stream, and hands it to the Ask drawer, which replays what came
 * so far and takes over the rest.
 */

/** What the search Ask row shows while the turn runs. */
export type SpeculativeProgress = {
  /** The latest step, labeled as in the drawer ("Searching knowledge base"). */
  step: string | null;
  /** The answer's first sentence, once text streams. */
  preview: string | null;
};

/** The run can't be taken over (it ended unsent, or the server refused); ask normally. */
export class SpeculativeAdoptError extends Error {
  constructor() {
    super('Speculative Ask turn ended before it was sent');
    this.name = 'SpeculativeAdoptError';
  }
}

/** How long a run handed to the drawer waits to be taken before it is cancelled. */
const HAND_OFF_TIMEOUT_MS = 5_000;

/** How long a cancel waits for a read run's seen request, so the server records the read. */
const SEEN_WAIT_MS = 2_000;

const MARKDOWN_NOISE: Array<[RegExp, string]> = [
  [/\[\[table:[^\]]*\]\]/g, ''], // inline table markers
  [/!?\[([^\]]*)\]\([^)]*\)/g, '$1'], // links and images keep their text
  [/[*_`~]+/g, ''], // emphasis and code marks
];

/**
 * The answer's first sentence as plain text, or what has streamed of it.
 * Headings and table rows are skipped, and bullet and quote marks dropped.
 */
export const firstSentence = (markdown: string): string | null => {
  let text = markdown
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line && !/^(#|\|)/.test(line) && !/^[-=:| ]+$/.test(line))
    .map((line) => line.replace(/^([-*+]|\d+\.|>)\s+/, ''))
    .join(' ');
  text = MARKDOWN_NOISE.reduce((out, [pattern, keep]) => out.replace(pattern, keep), text)
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) {
    return null;
  }
  const end = text.search(/[.!?](\s|$)/);
  return end >= 0 ? text.slice(0, end + 1) : text;
};

// A step has no label until its arguments arrive (the drawer shows just its
// icon), so the row keeps the last step that says something.
const latestStepLabel = (trace: AiQueryTraceEntry[]): string | null => {
  const steps = traceToSteps(trace).filter((step) => !step.isBookkeeping && step.label);
  return steps.length > 0 ? steps[steps.length - 1].label : null;
};

type Replay = (callbacks: AiQueryStreamCallbacks) => void;

export type SpeculativeRunOptions = {
  elevated: boolean;
  enableSourcegraph: boolean;
};

export class SpeculativeRun {
  readonly question: string;
  readonly startedAt = Date.now();
  handedOff = false;

  private readonly controller = new AbortController();
  /** Every callback the stream fired, to replay into the drawer on adoption. */
  private readonly events: Replay[] = [];
  /** The drawer's callbacks once adopted. */
  private target: AiQueryStreamCallbacks | null = null;
  private conversationId: string | null = null;
  private conversationWaiters: Array<() => void> = [];
  private result: AiQueryResult | null = null;
  private failure: unknown = null;
  /** The server ended the turn unsent. */
  private endedUnsent = false;
  private settle: {resolve: (r: AiQueryResult) => void; reject: (e: unknown) => void} | null = null;
  private trace: AiQueryTraceEntry[] = [];
  private answer = '';
  private progressListener: ((progress: SpeculativeProgress) => void) | null = null;
  private handOffTimer: ReturnType<typeof setTimeout> | null = null;
  /** The seen request, once the answer was read in the search pane. */
  private seen: Promise<unknown> | null = null;

  constructor(question: string, options: SpeculativeRunOptions) {
    this.question = question.trim();
    const record =
      <A extends unknown[]>(
        pick: (callbacks: AiQueryStreamCallbacks) => ((...args: A) => void) | undefined,
        local?: (...args: A) => void
      ) =>
      (...args: A) => {
        local?.(...args);
        const replay: Replay = (callbacks) => pick(callbacks)?.(...args);
        this.events.push(replay);
        if (this.target) {
          replay(this.target);
        }
      };

    streamAiQuery(this.question, {
      speculative: true,
      signal: this.controller.signal,
      elevated: options.elevated,
      enableSourcegraph: options.enableSourcegraph,
      onConversation: record(
        (c) => c.onConversation,
        (id: string) => {
          this.conversationId = id;
          this.wakeConversationWaiters();
        }
      ),
      onTurn: record((c) => c.onTurn),
      onText: record(
        (c) => c.onText,
        (delta: string, parent?: string) => {
          if (!parent) {
            this.answer += delta;
            this.notify();
          }
        }
      ),
      onToolStarted: record(
        (c) => c.onToolStarted,
        (name: string) => {
          this.trace = [...this.trace, pendingCall(name)];
          this.notify();
        }
      ),
      onToolCall: record(
        (c) => c.onToolCall,
        (call) => {
          this.trace = settlePendingCall(this.trace, call);
          this.notify();
        }
      ),
      onToolProgress: record(
        (c) => c.onToolProgress,
        (progress) => {
          this.trace = [...this.trace, {kind: 'progress', progress}];
          this.notify();
        }
      ),
      onToolResult: record(
        (c) => c.onToolResult,
        (result) => {
          this.trace = [...this.trace, {kind: 'result', result}];
          this.notify();
        }
      ),
      onDataset: record((c) => c.onDataset),
      onResults: record((c) => c.onResults),
      onDone: (result) => {
        this.result = result;
        this.settle?.resolve(result);
        this.notify();
      },
      onSpeculativeEnd: () => {
        this.endedUnsent = true;
        this.wakeConversationWaiters();
      },
    })
      .catch((error: unknown) => {
        this.failure = error;
        this.settle?.reject(error);
      })
      .finally(() => {
        // A stream that ends without the server's id can never be adopted.
        this.wakeConversationWaiters();
      });
  }

  /**
   * Everything streamed so far, for a view that shows the whole answer rather
   * than the one-line progress (the search box's answer pane).
   */
  get snapshot(): {answer: string; trace: AiQueryTraceEntry[]; done: boolean} {
    return {answer: this.answer, trace: this.trace, done: this.result !== null};
  }

  /** Whether the drawer can still take this run over. */
  get usable(): boolean {
    return (
      !this.endedUnsent &&
      !this.controller.signal.aborted &&
      (this.failure === null || this.result !== null)
    );
  }

  /** Follow the run's progress (one listener: the search Ask row). */
  onProgress(listener: ((progress: SpeculativeProgress) => void) | null): void {
    this.progressListener = listener;
    if (listener) {
      this.notify();
    }
  }

  /**
   * Stop an unsent run; the server drops it when the stream closes. No-op once
   * adopted. A read one closes once its seen request is in (or soon after).
   */
  cancel(): void {
    this.clearHandOffTimer();
    if (this.target) {
      return;
    }
    if (!this.seen) {
      this.controller.abort();
      return;
    }
    const abort = () => this.controller.abort();
    const timer = setTimeout(abort, SEEN_WAIT_MS);
    void this.seen.finally(() => {
      clearTimeout(timer);
      abort();
    });
  }

  /**
   * The answer was read in the search pane: the server records the turn,
   * marked not sent, when it ends unsent. Once per run; sending it is still
   * {@link adopt}.
   */
  markSeen(): void {
    if (this.seen || this.target || !this.conversationId || !this.usable) {
      return;
    }
    this.seen = markSpeculativeAiQuerySeen(this.conversationId).catch(() => false);
  }

  /**
   * Reserve the run for the drawer (search is about to close and would cancel
   * it). Cancelled if the drawer doesn't take it soon.
   */
  handOff(): void {
    this.handedOff = true;
    this.clearHandOffTimer();
    this.handOffTimer = setTimeout(() => {
      if (!this.target) {
        this.cancel();
      }
    }, HAND_OFF_TIMEOUT_MS);
  }

  /**
   * Send the turn and take it over: `callbacks` get every frame so far, then
   * the rest live. Resolves with the answer as soon as it is in; rejects with
   * {@link SpeculativeAdoptError} when the run can't be taken over (ask
   * normally then), or with the turn's own error. Aborting `callbacks.signal`
   * stops the turn until it has answered.
   */
  async adopt(callbacks: AiQueryStreamCallbacks): Promise<AiQueryResult> {
    this.clearHandOffTimer();
    this.progressListener = null;
    const conversationId = await this.waitForConversation();
    if (!conversationId || !this.usable) {
      this.cancel();
      throw new SpeculativeAdoptError();
    }
    const adopted = await adoptSpeculativeAiQuery(
      conversationId,
      Date.now() - this.startedAt
    ).catch(() => false);
    if (!adopted || !this.usable) {
      this.cancel();
      throw new SpeculativeAdoptError();
    }
    this.target = callbacks;
    this.events.forEach((replay) => replay(callbacks));
    callbacks.signal?.addEventListener('abort', () => {
      // After the answer the stream only waits for the server to save the
      // turn; closing it then would lose the history.
      if (!this.result) {
        this.controller.abort();
      }
    });
    if (this.result) {
      return this.result;
    }
    if (this.failure !== null) {
      throw this.failure;
    }
    return new Promise<AiQueryResult>((resolve, reject) => {
      this.settle = {resolve, reject};
    });
  }

  private waitForConversation(): Promise<string | null> {
    if (this.conversationId || !this.usable) {
      return Promise.resolve(this.conversationId);
    }
    return new Promise((resolve) => {
      this.conversationWaiters.push(() => resolve(this.conversationId));
    });
  }

  private wakeConversationWaiters(): void {
    const waiters = this.conversationWaiters;
    this.conversationWaiters = [];
    waiters.forEach((wake) => wake());
  }

  private notify(): void {
    this.progressListener?.({
      step: latestStepLabel(this.trace),
      preview: firstSentence(this.answer),
    });
  }

  private clearHandOffTimer(): void {
    if (this.handOffTimer !== null) {
      clearTimeout(this.handOffTimer);
      this.handOffTimer = null;
    }
  }
}

/** The tab's one speculative run. */
let current: SpeculativeRun | null = null;

/** Start a run for `question`, cancelling the tab's previous one. */
export const startSpeculativeRun = (
  question: string,
  options: SpeculativeRunOptions
): SpeculativeRun => {
  current?.cancel();
  current = new SpeculativeRun(question, options);
  return current;
};

/** Cancel the tab's run unless it was handed to the drawer. */
export const cancelSpeculativeRun = (run?: SpeculativeRun | null): void => {
  const target = run ?? current;
  if (!target || target.handedOff) {
    return;
  }
  target.cancel();
  if (current === target) {
    current = null;
  }
};

/**
 * Reserve the tab's run for the drawer when `question` is what it was started
 * for. True when there is one to take.
 */
export const handOffSpeculativeRun = (question: string): boolean => {
  if (!current || current.question !== question.trim() || !current.usable) {
    return false;
  }
  current.handOff();
  return true;
};

/** The drawer's side of {@link handOffSpeculativeRun}: the reserved run, once. */
export const takeSpeculativeRun = (question: string): SpeculativeRun | null => {
  const run = current;
  if (!run?.handedOff || run.question !== question.trim() || !run.usable) {
    return null;
  }
  current = null;
  return run;
};
